import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Calendar } from "@/components/ui/calendar";
import StatusPill from "@/components/StatusPill";
import { fmtDate, titleCase } from "@/lib/format";
import { Plus, CalendarDays, Send, Trash2, CheckCircle2, AlertTriangle, FastForward } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export default function Holidays() {
    const [holidays, setHolidays] = useState([]);
    const [openNew, setOpenNew] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/holidays").then(({ data }) => setHolidays(data));
    useEffect(() => { load(); }, []);

    const notifyNow = async (h) => {
        if (!window.confirm(`Send the holiday-schedule broadcast for ${h.name} now?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/holidays/${h.id}/notify-now`);
            const total = data.sent.reduce((s, r) => s + (r.audience_size || 0), 0);
            toast.success(`Sent to ${total} customers`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const runCycle = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/holidays/run-notify-cycle");
            toast.success(`Notify cycle processed ${data.processed} holiday${data.processed === 1 ? "" : "s"}`);
            load();
        } catch { toast.error("Failed"); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this holiday?")) return;
        try { await api.delete(`/holidays/${id}`); load(); toast.success("Deleted"); }
        catch { toast.error("Failed"); }
    };

    const applyShift = async (h) => {
        if (!window.confirm(`Shift all ${h.affected_route_days?.map((d) => d).join(", ")} customers forward ${h.shift_days || 1} day for ${h.name}?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/holidays/${h.id}/apply-shift`);
            toast.success(`${data.customers_shifted} customer${data.customers_shifted === 1 ? "" : "s"} shifted to ${data.shifted_to}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not apply shift");
        } finally { setBusy(false); }
    };

    const toggleAutoNotify = async (h) => {
        try {
            await api.patch(`/holidays/${h.id}`, { auto_notify: !h.auto_notify });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not update");
        }
    };

    // Build a set of date strings for Calendar highlight + decide default month
    const holidayDates = holidays
        .map((h) => { try { return new Date(h.date + "T00:00:00"); } catch { return null; } })
        .filter(Boolean);
    const defaultMonth = holidayDates.length ? holidayDates[0] : new Date();

    return (
        <div data-testid="holidays-page">
            <PageHeader
                title="Holiday Calendar"
                subtitle="Mark holidays once. The platform auto-notifies affected customers 3 days before each one."
                actions={
                    <>
                        <Button variant="outline" size="sm" onClick={runCycle} disabled={busy} data-testid="run-cycle-btn">
                            <Send className="w-4 h-4 mr-1" />Run notify cycle
                        </Button>
                        <NewHolidaySheet open={openNew} setOpen={setOpenNew} onSaved={() => { setOpenNew(false); load(); }} />
                    </>
                }
            />
            <div className="px-8 py-6 grid lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-1">
                    <CardContent className="p-6">
                        <div className="overline mb-1">Visual</div>
                        <h3 className="font-heading text-lg font-semibold mb-4">Calendar</h3>
                        <Calendar
                            mode="multiple"
                            selected={holidayDates}
                            defaultMonth={defaultMonth}
                            onSelect={() => {}}
                            data-testid="holidays-calendar"
                            className="rounded-md border border-border p-2"
                        />
                        <div className="mt-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="w-3 h-3 rounded-sm bg-primary" /> Holiday on calendar
                            </div>
                            <div>Cron runs daily at 07:00 UTC and notifies customers whose route day is affected.</div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                    <CardContent className="p-0">
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <div className="overline mb-1">Upcoming</div>
                                <h3 className="font-heading text-lg font-semibold">Holidays on the schedule</h3>
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">{holidays.length} total</div>
                        </div>
                        <div className="divide-y divide-border" data-testid="holidays-list">
                            {holidays.length === 0 && (
                                <div className="p-8 text-center text-sm text-muted-foreground">
                                    No upcoming holidays. Click "New holiday" to add one.
                                </div>
                            )}
                            {holidays.map((h) => (
                                <div key={h.id} className="p-5 flex items-center justify-between gap-4"
                                     data-testid={`holiday-row-${h.id}`}>
                                    <div className="flex items-center gap-4 min-w-0">
                                        <div className="w-14 h-14 rounded-md border border-border surface flex flex-col items-center justify-center shrink-0">
                                            <CalendarDays className="w-3 h-3 text-muted-foreground" />
                                            <div className="font-mono text-[11px] mt-0.5">{h.date.slice(5)}</div>
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-heading font-semibold truncate">{h.name}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                {fmtDate(h.date)} · affects {h.affected_route_days?.map(titleCase).join(", ") || "—"}
                                            </div>
                                            <div className="flex items-center gap-2 mt-1.5">
                                                {h.notification_sent ? (
                                                    <span className="text-[11px] inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                                        <CheckCircle2 className="w-3 h-3" />Notified
                                                    </span>
                                                ) : h.auto_notify ? (
                                                    <span className="text-[11px] inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                                        <AlertTriangle className="w-3 h-3" />Auto-notify {h.notify_days_ahead}d before
                                                    </span>
                                                ) : (
                                                    <StatusPill status="paused" />
                                                )}
                                                {h.shift_applied && (
                                                    <span className="text-[11px] inline-flex items-center gap-1 text-violet-600 dark:text-violet-400">
                                                        <FastForward className="w-3 h-3" />Shifted {h.shift_applied_count || 0} stops
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-1.5">
                                            <Switch checked={h.auto_notify} onCheckedChange={() => toggleAutoNotify(h)}
                                                    data-testid={`holiday-autonotify-${h.id}`} />
                                            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Auto</Label>
                                        </div>
                                        <Button variant="outline" size="sm" onClick={() => notifyNow(h)} disabled={busy}
                                                data-testid={`holiday-notify-btn-${h.id}`}>
                                            <Send className="w-3.5 h-3.5 mr-1" />Notify
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => applyShift(h)}
                                                disabled={busy || h.shift_applied}
                                                data-testid={`holiday-shift-btn-${h.id}`}>
                                            <FastForward className="w-3.5 h-3.5 mr-1" />
                                            {h.shift_applied ? "Shifted" : "Apply shift"}
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => remove(h.id)}
                                                data-testid={`holiday-delete-${h.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function NewHolidaySheet({ open, setOpen, onSaved }) {
    const [form, setForm] = useState({
        name: "", date: "", affected_route_days: [], notify_days_ahead: 3, auto_notify: true, message_override: "",
    });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleDay = (d) => set("affected_route_days", form.affected_route_days.includes(d)
        ? form.affected_route_days.filter((x) => x !== d)
        : [...form.affected_route_days, d]);

    const submit = async () => {
        if (!form.name || !form.date) return toast.error("Name and date required");
        try {
            await api.post("/holidays", {
                ...form,
                affected_route_days: form.affected_route_days.length ? form.affected_route_days : null,
            });
            toast.success("Holiday added");
            setForm({ name: "", date: "", affected_route_days: [], notify_days_ahead: 3, auto_notify: true, message_override: "" });
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button size="sm" data-testid="new-holiday-btn"><Plus className="w-4 h-4 mr-1" />New holiday</Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-lg" data-testid="new-holiday-sheet">
                <SheetHeader>
                    <SheetTitle className="font-heading text-2xl">New holiday</SheetTitle>
                    <SheetDescription className="sr-only">Add a holiday and configure notifications.</SheetDescription>
                </SheetHeader>
                <div className="space-y-3 mt-6">
                    <div className="space-y-1.5"><Label className="text-xs">Name</Label>
                        <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                               placeholder="e.g. Memorial Day" data-testid="hol-name" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Date</Label>
                        <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
                               data-testid="hol-date" /></div>
                    <div className="space-y-1.5">
                        <Label className="text-xs">Affected route days (defaults to the holiday's weekday)</Label>
                        <div className="grid grid-cols-3 gap-1.5">
                            {DAYS.map((d) => (
                                <button key={d} type="button" onClick={() => toggleDay(d)}
                                        data-testid={`hol-day-${d}`}
                                        className={`px-2 py-1.5 text-xs uppercase tracking-wider rounded border transition-colors ${
                                            form.affected_route_days.includes(d)
                                                ? "border-primary bg-primary/10 text-primary font-semibold"
                                                : "border-border text-muted-foreground hover:text-foreground"
                                        }`}>
                                    {d.slice(0, 3)}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs">Notify days ahead</Label>
                        <Input type="number" min="0" max="14" value={form.notify_days_ahead}
                               onChange={(e) => set("notify_days_ahead", parseInt(e.target.value || 0))}
                               data-testid="hol-days-ahead" /></div>
                    <div className="flex items-center justify-between p-3 border border-border rounded-md">
                        <div>
                            <div className="text-sm font-medium">Auto-notify</div>
                            <div className="text-[11px] text-muted-foreground">Send the holiday-schedule template automatically</div>
                        </div>
                        <Switch checked={form.auto_notify} onCheckedChange={(v) => set("auto_notify", v)} data-testid="hol-autonotify" />
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs">Custom message (optional)</Label>
                        <Textarea rows={3} value={form.message_override}
                                  onChange={(e) => set("message_override", e.target.value)}
                                  placeholder="Leave blank to use the default holiday template"
                                  data-testid="hol-message" /></div>
                    <Button onClick={submit} className="w-full mt-4" data-testid="hol-submit">Add holiday</Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
